'use client';

import { Suspense, useEffect, useMemo, useState } from 'react';
import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}


const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const ALLOWED_HOSTS_ENV = process.env.NEXT_PUBLIC_GA_ALLOWED_HOSTS;

function getAllowedHosts(): string[] {
  if (!ALLOWED_HOSTS_ENV) return [];
  return ALLOWED_HOSTS_ENV.split(',')
    .map((host) => host.trim())
    .filter(Boolean);
}

export function GoogleAnalytics() {
  return (
    <Suspense fallback={null}>
      <AnalyticsTracker />
    </Suspense>
  );
}

function AnalyticsTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [enabled, setEnabled] = useState(false);

  const allowedHosts = useMemo(getAllowedHosts, []);

  useEffect(() => {
    if (!MEASUREMENT_ID) return;
    if (typeof window === 'undefined') return;

    if (allowedHosts.length === 0 || allowedHosts.includes(window.location.hostname)) {
      setEnabled(true);
    }
  }, [allowedHosts]);

  useEffect(() => {
    if (!enabled || !MEASUREMENT_ID) return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    const search = searchParams.toString();
    const pagePath = search ? `${pathname}?${search}` : pathname;

    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [enabled, pathname, searchParams]);

  if (!enabled || !MEASUREMENT_ID) {
    return null;
  }

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`} strategy="afterInteractive" />
      <Script id="ga-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${MEASUREMENT_ID}', {
            page_path: window.location.pathname + window.location.search,
          });
        `}
      </Script>
    </>
  );
}
